'use no memo';
import '../../../styles/spiffTool.css';

import { Reimbursement, type User } from '@kalos/kalos-rpc';
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Form,
} from '@kalos/ui';
import Alert from '@mui/material/Alert';
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { debounce } from 'lodash';
import { type FC, useEffect, useMemo, useState } from 'react';

import { getLoadingColumns } from '../../../components/utils';
import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME, NULL_TIME, ROWS_PER_PAGE } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import {
  type ReimbursementFilter,
  useReimbursementDeleteMutation,
  useReimbursementListQuery,
  useReimbursementUpdateMutation,
} from '../../../hooks/react-query/useReimbursementQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { formatDate, usd } from '../../../tools/helpers';
import { Loader } from '../../Loader/main';
import { ReimbursementCreateForm } from './ReimbursementCreateForm';
import {
  getReimbursementSearchFilter,
  getReimbursementViewerRoles,
} from './reimbursementLog.utils';
import { ReimbursementSearchForm } from './ReimbursementSearchForm';
import {
  type ReimbursementSearchFormSchema,
  useReimbursementSearchForm,
} from './ReimbursementSearchForm.hooks';
import { ReimbursementStatusLabel } from './ReimbursementStatusesLabel';
import { ReimbursementTableActions } from './ReimbursementTableActions';
import {
  getDefaultReimbursementCreateValues,
  useCreateReimbursement,
} from './useCreateReimbursement';

export interface Props {
  filterProp?: Partial<ReimbursementSearchFormSchema>;
  reimbursementOwnerId: User['id'];
  onClose?: () => void;
}

const staticReimbursements = Array.from({ length: 3 }, (_, i) => Reimbursement.create());

const checkedDate = (date: string) => (date === NULL_TIME ? 'Not Completed' : formatDate(date));

export const ReimbursementComponent: FC<Props> = ({
  onClose,
  filterProp = {},
  reimbursementOwnerId,
}) => {
  const loggedUserId = useAuth().user.id;

  const [page, setPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);

  const ownerUserQuery = useUserQuery({
    filters: {
      id: reimbursementOwnerId,
    },
  });

  const searchForm = useReimbursementSearchForm({
    defaultValues: useMemo(() => {
      return ReimbursementSearchForm.getDefaultValues({
        ...filterProp,
      });
    }, [filterProp]),
  });

  const [filter, setFilter] = useState<Partial<ReimbursementSearchFormSchema>>(
    searchForm.getValues(),
  );

  useEffect(() => {
    const debouncedUpdateSearch = debounce((data: Partial<ReimbursementSearchFormSchema>) => {
      setFilter((filter) => ({ ...filter, ...data }));
      setPage(0);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);

    const subscription = searchForm.watch(debouncedUpdateSearch);

    return () => subscription.unsubscribe();
  }, [searchForm]);

  const reimbursementFilter = useMemo<ReimbursementFilter>(() => {
    return getReimbursementSearchFilter({ ownerId: reimbursementOwnerId, page, search: filter });
  }, [filter, page, reimbursementOwnerId]);

  const loggedInUserQuery = useUserQuery({
    filters: {
      id: loggedUserId,
    },
    select(data) {
      return getReimbursementViewerRoles(data, loggedUserId, reimbursementOwnerId);
    },
  });

  const updateReimbursementMutation = useReimbursementUpdateMutation();
  const deleteReimbursementMutation = useReimbursementDeleteMutation();

  const hasAccessToViewData = loggedInUserQuery.data?.hasPermission;
  const reimbursementListQuery = useReimbursementListQuery({
    filter: reimbursementFilter,
    enabled: hasAccessToViewData,
  });

  useEffect(() => {
    if (reimbursementListQuery.isSuccess) {
      setTotalEntries(reimbursementListQuery.data?.totalCount || 0);
    }
  }, [reimbursementListQuery.data?.totalCount, reimbursementListQuery.isSuccess]);

  const tableColumns = useMemo<ColumnDef<Reimbursement>[]>(() => {
    return [
      {
        accessorKey: 'description',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Description" />;
        },
        meta: {
          className: 'w-48 text-sm',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              {props.row.original.description}
            </div>
          );
        },
      },
      {
        accessorKey: 'amount',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Amount" />;
        },
        meta: {
          className: 'w-24 text-sm',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              {usd(props.row.original.amount)}
            </div>
          );
        },
      },
      {
        accessorKey: 'jobNumber',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Job Number" />;
        },
        meta: {
          className: 'w-24 text-sm',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              {props.row.original.jobNumber ? props.row.original.jobNumber : '-'}
            </div>
          );
        },
      },
      {
        accessorKey: 'createdDate',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Creation Date" />;
        },
        meta: {
          className: 'w-24 text-sm',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              {checkedDate(props.row.original.createdDate)}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Status" />;
        },
        meta: {
          className: 'w-40 flex gap-1 flex-wrap ',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <ReimbursementStatusLabel id={props.row.original.statusId} />
            </div>
          );
        },
      },
      {
        accessorKey: 'reviewDate',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Admin Action Date" />;
        },
        meta: {
          className: 'w-24 text-sm',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              {checkedDate(props.row.original.reviewDate)}
            </div>
          );
        },
      },
      {
        accessorKey: 'processedDate',
        header(props) {
          return <DataTableColumnHeader column={props.column} title="Processed Date" />;
        },
        meta: {
          className: 'w-24 text-sm',
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              {checkedDate(props.row.original.processedDate)}
            </div>
          );
        },
      },
      {
        accessorKey: 'actions',
        meta: {
          className: 'w-36 flex gap-1 justify-center items-center flex-wrap text-sm',
        },
        header(props) {
          return null;
        },
        cell(props) {
          return (
            <div className={props.column.columnDef.meta?.className}>
              <ReimbursementTableActions
                reimbursement={props.row.original}
                isManager={loggedInUserQuery.data?.isManager ?? false}
                isPayroll={loggedInUserQuery.data?.isPayroll ?? false}
                isOwner={loggedUserId === props.row.original.userId}
              />
            </div>
          );
        },
      },
    ];
  }, [loggedInUserQuery.data?.isManager, loggedInUserQuery.data?.isPayroll, loggedUserId]);

  const loadingColumns = useMemo(() => getLoadingColumns(tableColumns), [tableColumns]);
  const loading =
    loggedInUserQuery.isPending ||
    ownerUserQuery.isPending ||
    reimbursementListQuery.isPending ||
    updateReimbursementMutation.isPending ||
    deleteReimbursementMutation.isPending;

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    data: loading ? staticReimbursements : reimbursementListQuery.data?.results ?? [],
    columns: loading ? loadingColumns : tableColumns,
    enableSorting: false,
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Shared with the phone: S3 upload, file record, the reimbursement, then its document.
  const { createReimbursement: handleCreateReimbursement, isSaving } = useCreateReimbursement({
    loggedUserId,
    onCreated: () => setIsCreateDialogOpen(false),
  });

  const defaultCreateFormValues = useMemo<
    React.ComponentProps<typeof ReimbursementCreateForm>['defaultValues']
  >(() => getDefaultReimbursementCreateValues(reimbursementOwnerId), [reimbursementOwnerId]);

  if (loggedInUserQuery.isPending) return <Loader />;

  const pageCount = Math.ceil(totalEntries / ROWS_PER_PAGE);
  return loggedInUserQuery.isSuccess ? (
    <div className="flex flex-col gap-2 pt-6">
      <div className="flex flex-wrap justify-between gap-2 px-4">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-medium">
            Reimbursables {ownerUserQuery.data?.firstname} {ownerUserQuery.data?.lastname}
          </h1>
          <DataTablePagination
            currentPage={page}
            setPage={setPage}
            pageCount={pageCount}
            className="max-w-max"
          />
        </div>
        <div className="flex gap-1">
          {onClose && <Button onClick={onClose}>Close</Button>}
          <Dialog onOpenChange={setIsCreateDialogOpen} open={isCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>Reimbursement Apply</Button>
            </DialogTrigger>

            <DialogContent className="@container max-h-svh max-w-2xl overflow-auto pb-2 md:h-auto md:max-h-[90vh]">
              <DialogTitle>Create Reimbursement</DialogTitle>
              <ReimbursementCreateForm
                onSave={handleCreateReimbursement}
                isLoading={isSaving}
                disabled={isSaving}
                disableTechnicianPicker
                defaultValues={defaultCreateFormValues}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Form {...searchForm}>
        <div className="px-4">
          <ReimbursementSearchForm />
        </div>
      </Form>

      <div className="max-w-[100vw] overflow-auto">
        <DataTable table={table} />
      </div>
    </div>
  ) : (
    <Alert severity="error">You don&apos;t have permission to view this Log</Alert>
  );
};
